import { TodoService } from 'api/services/TodoService'
import { Node } from 'prosemirror-model'
import { EditorState, Plugin, Transaction } from 'prosemirror-state'
import { ReplaceStep } from 'prosemirror-transform'
import { type EditorView } from 'prosemirror-view'
import store from 'redux/store'
import { createDecorations } from './decorations'
import { TodosView } from './TodosView'
import { State, InitConfig, TaskMarkDataset } from './Types'
import { targetTaskMark } from './utils'
import { getTodosPluginState, TODOS_PLUGIN_KEY } from './constants'
import { TASKS_VIEW } from 'components/DocRightRail/Constants'

// Creates a Prosemirror plugin that enables interactions of inline todos with
// the surrounding application.
//
// More specifically, it handles the following behaviours:
//
// - When deleting the text of an inline todo, delete the corresponding todo.
// - When undoing the deletion of text corresponding to a todo, resurrect the
//   todo.
// - Show a tooltip with the todo identifier on hover and click
// - Overrides the default task nodeview with a custom nodeview that applies
//   the correct styling depending on the status of the corresponding todo
export const TodosPlugin = (config: InitConfig): Plugin => {
  return new Plugin({
    key: TODOS_PLUGIN_KEY,
    state: {
      init() {
        return {
          activeTodoContainer: bootstrapContainer(),
          highlightedTodoContainer: bootstrapContainer(),
          docStore: config.docStore,
          queryClient: config.queryClient,
          enabled: config.enabled,
        }
      },
      apply(tx: Transaction, state: State, oldEditorState: EditorState) {
        syncTodosFromTransaction(tx, oldEditorState)

        const meta = tx.getMeta(TODOS_PLUGIN_KEY)
        if (!meta) return state

        return { ...state, enabled: meta.enabled }
      },
    },
    view(view: EditorView) {
      return new TodosView(view)
    },
    props: {
      decorations(state) {
        return createDecorations(state)
      },
      handleClick: (view, _pos, event) => {
        const el = targetTaskMark(event)
        if (!el) {
          return false
        }
        const { uuid } = el.dataset as TaskMarkDataset
        const { docStore }: State = getTodosPluginState(view.state)
        docStore?.rootStore.todos.select(docStore.rootStore.todos.getByUuid(uuid)?.id)
        docStore?.collabItemsStore.select(uuid)
        docStore?.rightRailStore.setActiveTab(TASKS_VIEW)
        docStore?.rightRailStore.openSection('todos')

        return true
      },
      handleDOMEvents: {
        click: (view, event) => {
          if ('touchstart' in window) {
            return false
          }
          const el = targetTaskMark(event)
          if (!el) {
            return false
          }
          const { uuid } = el.dataset as TaskMarkDataset
          const { docStore }: State = getTodosPluginState(view.state)
          docStore?.rootStore.todos.select(docStore.rootStore.todos.getByUuid(uuid)?.id)
          docStore?.collabItemsStore.select(uuid)
          docStore?.rightRailStore.setActiveTab(TASKS_VIEW)
          docStore?.rightRailStore.openSection('todos')

          return true
        },
        touchstart: (view, event) => {
          const el = targetTaskMark(event)
          if (!el) {
            return false
          }
          const { uuid } = el.dataset as TaskMarkDataset
          const { docStore }: State = getTodosPluginState(view.state)
          docStore?.rootStore.todos.select(docStore.rootStore.todos.getByUuid(uuid)?.id)
          docStore?.collabItemsStore.select(uuid)
          docStore?.rightRailStore.setActiveTab(TASKS_VIEW)
          docStore?.rightRailStore.openSection('todos')

          return true
        },
        mouseout: (view, event) => {
          const el = targetTaskMark(event)
          if (!el) {
            return false
          }

          const { docStore }: State = getTodosPluginState(view.state)
          docStore?.rootStore.todos.highlight(undefined)

          return false
        },
        mouseover: (view, event) => {
          const el = targetTaskMark(event)
          if (!el) {
            return false
          }

          const { uuid } = el.dataset as TaskMarkDataset
          const { docStore }: State = getTodosPluginState(view.state)
          docStore?.rootStore.todos.highlight(docStore.rootStore.todos.getByUuid(uuid)?.id)

          return false
        },
      },
    },
  })
}

/*
 * This function is responsible for detecting todos that have been deleted or added back by the given transaction.
 *
 * To detect deleted todos, it checks all the nodes between the `from` and `to`
 * of the replace step that have a task mark. These nodes are being replaced,
 * so the todo is considered deleted.
 *
 * To detect resurrected todos, it checks if the new content of the replace
 * step contains task marks. If it does, it means these todos are being added
 * back into the document (most often via undo of a previous deletion), so they
 * are considered undeleted.
 */
const syncTodosFromTransaction = (tx: Transaction, previousEditorState: EditorState) => {
  const replaceSteps = tx.steps.filter((step) => step instanceof ReplaceStep)

  const deletedTodosUuids: string[] = []
  const resurrectedTodosUuids: string[] = []

  if (replaceSteps.length > 0 && tx.getMeta('source') !== 'remote') {
    for (const step of replaceSteps) {
      const { from: replaceStart, to: replaceEnd } = step.toJSON()
      if (replaceStart === replaceEnd) {
        continue
      }
      // If the replace step is removing entire nodes with a todo mark or todo
      // list items create a list of the deleted todos uuids so we can delete
      // them in the background.
      if (previousEditorState.doc.nodeSize - 1 > replaceEnd) {
        previousEditorState.doc.nodesBetween(replaceStart, replaceEnd, (node, pos) => {
          if (pos >= replaceStart && pos + node.nodeSize <= replaceEnd) {
            const todoMark = node.marks.find((mark) => mark.type.name === previousEditorState.schema.marks.task.name)
            if (todoMark) {
              deletedTodosUuids.push(todoMark.attrs.uuid)
            }

            if (node.type === previousEditorState.schema.nodes.todoListItem && node.attrs.todoId) {
              deletedTodosUuids.push(node.attrs.uuid)
            }
          }
        })
      }

      // If the replace step is adding back todo marks or todo list items
      // create a list of the resurrected todos uuids so we can resurrect them
      // in the background.
      ;(step as any).slice.content.descendants((node: Node) => {
        const todoMark = node.marks.find((mark) => mark.type.name === previousEditorState.schema.marks.task.name)
        if (todoMark) {
          resurrectedTodosUuids.push(todoMark.attrs.uuid)
        }

        if (node.type === previousEditorState.schema.nodes.todoListItem && node.attrs.todoId) {
          resurrectedTodosUuids.push(node.attrs.uuid)
        }
      })
    }
  }
  if (deletedTodosUuids.length > 0) {
    TodoService.batchDelete(store, deletedTodosUuids)
  }
  if (resurrectedTodosUuids.length > 0) {
    TodoService.batchUndelete(store, resurrectedTodosUuids)
  }
}

const bootstrapContainer = () => {
  const containerDom = document.createElement('div')
  return containerDom
}
